import React from 'react';
import { fmtMoney, fmtDate, taxRateToPercent, previewItems } from './format';

const DARK = '#22323F';
const DEFAULT_ACCENT = '#A8D24A';

/**
 * Modèle "Classique" — bandeau vert/sombre, table à en-tête foncé,
 * total mis en avant et pied de page sombre (inspiré de l'exemple WestPharmacy).
 * Document à largeur fixe (≈ A4) rendu tel quel dans l'aperçu et le PDF.
 */
export default function ClassicTemplate({ invoice = {}, branding = {} }) {
  const accent = branding.brandColor || DEFAULT_ACCENT;
  const currency = invoice.currency || 'USD';
  const items = previewItems(invoice.items);

  const companyName = branding.companyName || 'Votre entreprise';
  const initials = (companyName.trim()[0] || 'F').toUpperCase();

  const cell = { padding: '10px 14px', fontSize: 13, color: DARK };
  const labelStyle = { fontSize: 10, fontWeight: 700, color: '#8a97a3', letterSpacing: 0.4, textTransform: 'uppercase' };
  const valueStyle = { fontSize: 13, color: DARK, marginTop: 3, fontWeight: 600 };

  const Field = ({ label, value }) => (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value || '—'}</div>
    </div>
  );

  return (
    <div style={{ width: 720, background: '#fff', color: DARK, fontFamily: 'Arial, Helvetica, sans-serif', position: 'relative', overflow: 'hidden' }}>
      {/* Coin angulaire vert */}
      <div style={{ position: 'absolute', top: 0, right: 0, width: 260, height: 96, background: accent, clipPath: 'polygon(28% 0, 100% 0, 100% 100%, 0 0)' }} />

      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '34px 40px 18px' }}>
        <div>
          <div style={{ fontSize: 38, fontWeight: 800, letterSpacing: 1, color: DARK }}>INVOICE</div>
        </div>
        <div style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 12, background: DARK, borderRadius: 40, padding: '8px 18px 8px 8px' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: DARK, fontWeight: 800, fontSize: 18 }}>{initials}</span>
            )}
          </div>
          <div style={{ color: '#fff', lineHeight: 1.1 }}>
            <div style={{ fontWeight: 800, fontSize: 16, textTransform: 'uppercase' }}>{companyName}</div>
            {branding.tagline && <div style={{ fontSize: 9, color: accent }}>{branding.tagline}</div>}
          </div>
        </div>
      </div>

      {/* Billed to */}
      <div style={{ padding: '6px 40px 0' }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: DARK }}>Facturé à</div>
        <div style={{ width: 90, height: 3, background: accent, marginTop: 6, marginBottom: 18, borderRadius: 2 }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', rowGap: 16, columnGap: 16 }}>
          <Field label="Client" value={invoice.clientName} />
          <Field label="Téléphone" value={invoice.clientPhone} />
          <Field label="Date d'émission" value={fmtDate(invoice.issueDate || invoice.createdAt || Date.now())} />
          <Field label="Email" value={invoice.clientEmail} />
          <Field label="Échéance" value={fmtDate(invoice.dueDate)} />
          <Field label="N° de facture" value={invoice.invoiceNumber || '—'} />
        </div>
      </div>

      {/* Table */}
      <div style={{ padding: '24px 40px 0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 6, overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: DARK, color: '#fff' }}>
              <th style={{ ...cell, color: '#fff', textAlign: 'left', width: 48 }}>Qté</th>
              <th style={{ ...cell, color: '#fff', textAlign: 'left' }}>Description</th>
              <th style={{ ...cell, color: '#fff', textAlign: 'right', width: 110 }}>Prix</th>
              <th style={{ padding: '10px 14px', fontSize: 13, color: DARK, textAlign: 'right', width: 120, background: accent, fontWeight: 800 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ ...cell, textAlign: 'center', color: '#9aa6b0', fontStyle: 'italic' }}>
                  Aucune ligne pour le moment
                </td>
              </tr>
            ) : (
              items.map((it, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#f5f8ee' : '#fff' }}>
                  <td style={{ ...cell, textAlign: 'left' }}>{it.quantity}</td>
                  <td style={{ ...cell, textAlign: 'left' }}>{it.description || '—'}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>{fmtMoney(it.unitPrice, currency)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{fmtMoney(it.total, currency)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bas : conditions + récap */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '26px 40px 36px', alignItems: 'flex-start' }}>
        <div style={{ maxWidth: 320 }}>
          <div style={{ ...labelStyle, marginBottom: 6 }}>Conditions de paiement</div>
          <div style={{ fontSize: 11, color: '#5b6770', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {invoice.notes || 'Merci de régler avant la date d\'échéance via le lien de paiement fourni.'}
          </div>
        </div>

        <div style={{ width: 280 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#5b6770' }}>
            <span>Sous-total</span>
            <span>{fmtMoney(invoice.subtotal, currency)}</span>
          </div>
          {Number(invoice.discount) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#5b6770' }}>
              <span>Remise</span>
              <span>-{fmtMoney(invoice.discount, currency)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#5b6770', borderBottom: '1px solid #e6ebe0' }}>
            <span>TVA ({taxRateToPercent(invoice.taxRate)}%)</span>
            <span>{fmtMoney(invoice.taxAmount, currency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, background: accent, borderRadius: 6, padding: '12px 16px' }}>
            <span style={{ fontWeight: 800, color: DARK, fontSize: 15 }}>Total</span>
            <span style={{ fontWeight: 800, color: DARK, fontSize: 18 }}>{fmtMoney(invoice.total, currency)}</span>
          </div>
        </div>
      </div>

      {/* Pied de page sombre */}
      <div style={{ position: 'relative', background: DARK, color: '#fff', padding: '16px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: 160, height: 60, background: accent, clipPath: 'polygon(0 100%, 0 0, 70% 100%)', opacity: 0.9 }} />
        <span style={{ position: 'relative', zIndex: 2 }}>{branding.companyPhone || '+0 000 000 000'}</span>
        <span style={{ position: 'relative', zIndex: 2 }}>{branding.companyEmail || 'contact@entreprise.com'}</span>
        <span style={{ position: 'relative', zIndex: 2, textAlign: 'right', maxWidth: 220 }}>{branding.companyAddress || 'Adresse de l\'entreprise'}</span>
      </div>
    </div>
  );
}
